import React, { useEffect, useState } from "react";
import { Loader2, Save, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { RULE_FIELDS, inr } from "./shared";

/** Read-only rules card (admin / collector portals). */
export function RulesSummaryCard({ rules, compact = false, testId = "attendance-rules-card" }) {
  if (!rules) return null;
  const rows = [
    ["Visits required / day", rules.visit_threshold],
    ["Collection required / day", inr(rules.collection_threshold)],
    ["Late sign-in after", `${rules.effective_cutoff_time} IST${rules.late_grace_minutes ? ` (+${rules.late_grace_minutes} min grace)` : ""}`],
    ["Late sign-ins tolerated / month", `${rules.late_login_limit}${rules.late_after_limit_half_day ? " — then Half Day" : ""}`],
    ["Partial-day rule", rules.partial_half_day_enabled ? `Half Day at ≥ ${rules.partial_half_day_pct}% of either requirement` : "Off — requirement not met = Absent"],
    ["Sundays", rules.sunday_weekly_off ? "Weekly off (admin may declare a working Sunday)" : "Normal working days"],
    ["Finalised daily at", `${rules.finalise_time} IST`],
    ["Notifications", rules.notify_half_day_absent ? "In-app notice to the collector on Half Day / Absent" : "Off"],
  ];
  return (
    <Card className="shadow-sm" data-testid={testId}>
      <CardContent className={compact ? "p-3" : "p-4"}>
        <p className="text-sm font-medium flex items-center gap-2 mb-2"><SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden="true" /> Attendance rules
          {rules.is_default ? <Badge variant="outline" className="text-[10px]">Platform defaults</Badge> : <Badge variant="secondary" className="text-[10px]">Configured by Super Admin</Badge>}</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 border-b border-dashed py-1"><dt className="text-muted-foreground text-xs">{k}</dt><dd className="font-medium text-right text-xs" data-testid={`rule-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{v}</dd></div>)}
        </dl>
        <p className="text-[11px] text-muted-foreground mt-2">Field duty days ignore the late sign-in rule but still need the requirement. Overrides, holidays, working Sundays and leave decisions are all logged with the reason, the admin's name and the time.
          {rules.updated_at && <> Last changed {fmtDateTime(rules.updated_at)}{rules.updated_by_name ? ` by ${rules.updated_by_name}` : ""}.</>}</p>
      </CardContent>
    </Card>
  );
}

/** Super Admin editor (Tenant Configuration → Attendance Rules). */
export function AttendanceRulesForm({ code }) {
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const load = () => api.get(`/platform/municipalities/${code}/attendance-rules`).then((r) => { setRules(r.data); setForm(pick(r.data)); setError(null); }).catch((e) => setError(errMsg(e)));
  useEffect(() => { if (code) { setRules(null); setForm(null); load(); } }, [code]); // eslint-disable-line
  const pick = (r) => Object.fromEntries(RULE_FIELDS.map((f) => [f.key, r[f.key] ?? (f.type === "switch" ? false : "")]));
  const dirty = rules && form && JSON.stringify(pick(rules)) !== JSON.stringify(form);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      ["visit_threshold", "collection_threshold", "late_grace_minutes", "late_login_limit", "partial_half_day_pct"].forEach((k) => { body[k] = body[k] === "" ? null : Number(body[k]); });
      if (body.late_cutoff_time === "") body.late_cutoff_time = null;
      const r = await api.put(`/platform/municipalities/${code}/attendance-rules`, body);
      setRules(r.data); setForm(pick(r.data));
      toast.success("Attendance rules saved", { description: r.data.summary });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const reset = () => setForm(pick({ ...rules, ...rules.defaults, late_cutoff_time: "" }));
  if (error) return <p className="text-sm text-destructive" data-testid="attendance-rules-error">{error}</p>;
  if (!form) return <p className="text-sm text-muted-foreground">Loading rules…</p>;
  return (
    <form onSubmit={save} className="space-y-4" data-testid="attendance-rules-form">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground max-w-2xl">These rules drive every collector's daily attendance for this municipality: the visit / collection requirement, what counts as a late sign-in, how many late sign-ins are tolerated per month before Half Day, the optional partial-day rule, and when the previous day is finalised. {rules.is_default ? "Currently on platform defaults." : `Last saved ${fmtDateTime(rules.updated_at)} by ${rules.updated_by_name || "Super Admin"}.`}</p>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="attendance-rules-reset">Reset to defaults</Button>
          <Button type="submit" size="sm" disabled={busy || !dirty} data-testid="attendance-rules-save">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save rules</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {RULE_FIELDS.map((f) => (
          <Card key={f.key} className="shadow-sm"><CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor={`rule-${f.key}`} className="text-sm">{f.label}</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">{f.hint}</p>
              </div>
              {f.type === "switch" ? (
                <Switch id={`rule-${f.key}`} checked={!!form[f.key]} onCheckedChange={(v) => setForm((x) => ({ ...x, [f.key]: v }))} data-testid={`rule-input-${f.key}`} />
              ) : (
                <Input id={`rule-${f.key}`} type={f.type} value={form[f.key] ?? ""} min={f.min} max={f.max} step={f.key === "collection_threshold" ? "100" : "1"}
                  onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))} className="h-9 w-[140px] shrink-0" placeholder={f.type === "time" && f.key === "late_cutoff_time" ? rules.duty_start_time : ""}
                  disabled={f.key === "partial_half_day_pct" && !form.partial_half_day_enabled} data-testid={`rule-input-${f.key}`} />
              )}
            </div>
          </CardContent></Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Current effect: {rules.summary}</p>
    </form>
  );
}
